// Skill Builder remediation: the picker a tutor assigns worksheets from.
//
// This is a picker, not a recommended sequence. It lists only the skills THIS
// student failed, and only the worksheet that matches the misconception they
// showed.
//
// The list is grouped by STRAND and nothing else. It used to be a Year >
// Cluster > Topic > Subtopic drill-down; tutors were opening four accordions to
// reach one worksheet, so the curriculum path moved onto the row as a label.
//
// Before changing the shape: the list is BIG. One Year 6 diagnostic produced
// 183 failed skills spanning seven year levels, so the search box is
// load-bearing and everything starts collapsed. Selecting a whole strand at
// once is deliberate; selecting EVERYTHING across every strand is not offered,
// because a hundred worksheets in one lesson is not a plan.
//
// A failed skill with no worksheet authored yet is still listed, greyed out.
// Hiding it would make a content gap look like a skill the student passed.
//
// The list itself lives in BmdSkillbuilderStrandList.

import React, { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import Select, { SingleValue } from "react-select"
import { Col, Row } from "reactstrap"
import { useBmdPrereqCandidates } from "../hooks/useBmdPrereq"
import { useBmdSkillbuilderPicker } from "../hooks/useBmdSkillbuilders"
import { BmdSkillbuilderStrandList } from "./BmdSkillbuilderStrandList"
import { BmdSkillbuilderStats } from "./BmdSkillbuilderStats"
import { BmdSkillbuilderPreviewModal } from "./BmdSkillbuilderPreviewModal"
import {
  allRows,
  groupRowsByStrand,
  isStageable,
  type BmdSkillbuilderRow,
  type BmdSkillbuilderStrandGroup,
} from "../utils/skillbuilder-rows"
import type { BmdPrereqCandidate } from "../types/bmd-prereq.types"
import type {
  BmdSkillbuilderItem,
  BmdSkillbuilderStagedDraft,
  BmdSkillbuilderYear,
} from "../types/bmd-skillbuilder.types"
import { BMD_COLOURS } from "../constants/bmd.colours"

// ---------------------------------------------------------------------------
// Design tokens, from the PM's mockup
// ---------------------------------------------------------------------------

const INK = BMD_COLOURS.INK
// A step darker than they started. The QA kept reporting the small grey text
// across the Add Activity tabs as unreadable, so both moved together and the
// sibling tabs match BmdPrereqTabPanel. MUTED is still a clear step off INK,
// so the states that use it to mean "absent" or "disabled" still read that way.
const BODY = BMD_COLOURS.BODY
const MUTED = BMD_COLOURS.MUTED
const RULE = "#e9edf3"
const ACCENT = BMD_COLOURS.ACCENT
const CARD: React.CSSProperties = {
  backgroundColor: "#fff",
  border: `1px solid ${RULE}`,
  borderRadius: 10,
  padding: 20,
}

const LABEL: React.CSSProperties = {
  color: INK,
  fontSize: 12.5,
  fontWeight: 700,
  marginBottom: 8,
  display: "block",
}

const SELECT_STYLES = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
  control: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: "40px",
    fontSize: "13px",
  }),
}

type CandidateOption = {
  value: string
  label: string
  candidate: BmdPrereqCandidate
}

/** "31 Aug 2026". Fixed rather than locale-default, which gives "8/31/2026". */
function formatDate(iso: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}

type BmdSkillbuilderTabPanelProps = {
  studentKey: string
  /** Skill Builders already staged in this visit, so we don't offer them twice. */
  stagedSkillbuilderIds?: number[]
  onStageDraft: (draft: BmdSkillbuilderStagedDraft) => void
}

function candidateLabel(c: BmdPrereqCandidate): string {
  const year = c.year_level_name || "Diagnostic"
  const when = formatDate(c.completed_on || c.created_on)
  // Just the count, not "n of m": here the tutor is picking which diagnostic
  // to remediate, and the denominator is not what they are weighing up.
  return `${year}${when ? ` · ${when}` : ""} · ${c.wrong_base_count} incorrect`
}

/** Matches a search term against a skill and the branch it sits on. */
function matchesSearch(
  term: string,
  item: BmdSkillbuilderItem,
  branch: string[]
): boolean {
  if (!term) return true
  const needle = term.toLowerCase()
  if (item.skill_name.toLowerCase().includes(needle)) return true
  return branch.some(name => (name || "").toLowerCase().includes(needle))
}

/**
 * Drops everything the search term does not reach, keeping the tree shape so
 * the branches that survive still open the same way.
 */
function filterYears(
  years: BmdSkillbuilderYear[],
  term: string
): BmdSkillbuilderYear[] {
  if (!term) return years
  const kept: BmdSkillbuilderYear[] = []

  for (const year of years) {
    const clusters = []
    for (const cluster of year.clusters) {
      const topics = []
      for (const topic of cluster.topics) {
        const subs = []
        for (const sub of topic.sub_topics) {
          const branch = [
            year.year_name,
            cluster.strand_name,
            topic.topic_name,
            sub.sub_topics_name,
          ]
          const items = sub.skillbuilders.filter(i =>
            matchesSearch(term, i, branch)
          )
          if (items.length > 0) subs.push({ ...sub, skillbuilders: items })
        }
        if (subs.length > 0) topics.push({ ...topic, sub_topics: subs })
      }
      if (topics.length > 0) clusters.push({ ...cluster, topics })
    }
    if (clusters.length > 0) kept.push({ ...year, clusters })
  }
  return kept
}

export function BmdSkillbuilderTabPanel({
  studentKey,
  stagedSkillbuilderIds = [],
  onStageDraft,
}: BmdSkillbuilderTabPanelProps) {
  const {
    candidates,
    loading: loadingCandidates,
    error: candidatesError,
  } = useBmdPrereqCandidates(studentKey)
  const [selected, setSelected] = useState<SingleValue<CandidateOption>>(null)
  const [searchTerm, setSearchTerm] = useState<string>("")

  const selectedKey = selected?.candidate.diagnostics_session_key || ""
  const { picker, loading, error } = useBmdSkillbuilderPicker(
    selectedKey,
    studentKey
  )

  // One strand open at a time, the way the Online Activities accordions behave.
  const [openStrand, setOpenStrand] = useState<string>("")

  // Worksheets ticked but not yet added. Keyed by skillbuilder_id, which is
  // what staging uses, so a skill appearing in two year levels cannot be added
  // twice under one id.
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set())

  const [previewing, setPreviewing] = useState<BmdSkillbuilderItem | null>(null)

  // Only a change of diagnostic clears this. A search must NOT close the list,
  // because filtering and then collapsing leaves the tutor reopening the strand
  // they just searched into; and it must not drop the ticks either, because
  // searching is how a tutor finds the next one to tick.
  useEffect(() => {
    setOpenStrand("")
    setCheckedIds(new Set())
  }, [selectedKey])

  const options: CandidateOption[] = useMemo(
    () =>
      candidates
        .filter(c => c.wrong_base_count > 0)
        .map(c => ({
          value: c.diagnostics_session_key,
          label: candidateLabel(c),
          candidate: c,
        })),
    [candidates]
  )

  const stagedSet = useMemo(
    () => new Set(stagedSkillbuilderIds),
    [stagedSkillbuilderIds]
  )

  const trimmedSearch = searchTerm.trim()
  // A one-character search matches nearly everything, so the tree is only held
  // open once the term is specific enough for that to be useful.
  const isSearching = trimmedSearch.length >= 2

  const years = useMemo(
    () => filterYears(picker?.years || [], trimmedSearch),
    [picker, trimmedSearch]
  )

  // Strand > worksheet, with the curriculum path carried on each row.
  const groups = useMemo(() => groupRowsByStrand(years), [years])

  const stageRow = (row: BmdSkillbuilderRow) => {
    const item = row.item
    if (!item.has_skillbuilder || item.skillbuilder_id == null) return
    onStageDraft({
      draft_key: `sb-${item.skillbuilder_id}`,
      diagnostics_session_key: selectedKey,
      skillbuilder_id: item.skillbuilder_id,
      skill_id: item.skill_id,
      skill_name: item.skill_name,
      skillbuilder_name: item.skillbuilder_name,
      year_name: row.year_name,
    })
  }

  const handleStage = (row: BmdSkillbuilderRow) => {
    stageRow(row)
    // Adding one directly clears its tick, or it would still look pending.
    if (row.item.skillbuilder_id != null) {
      setCheckedIds(prev => {
        const next = new Set(prev)
        next.delete(row.item.skillbuilder_id as number)
        return next
      })
    }
    // "Added to Skill Builders", the panel on the right it actually lands in -
    // not "Assigned Diagnostics", which holds base tests, and not "staged",
    // which reads as finished when Add Activities is still to come.
    toast.success("Added to Skill Builders", { autoClose: 2000 })
  }

  const toggleCheck = (skillbuilderId: number) =>
    setCheckedIds(prev => {
      const next = new Set(prev)
      if (next.has(skillbuilderId)) next.delete(skillbuilderId)
      else next.add(skillbuilderId)
      return next
    })

  /** Whole strand on or off, over the rows that CAN be staged. */
  const toggleStrand = (group: BmdSkillbuilderStrandGroup) => {
    const ids = group.rows
      .filter(r => isStageable(r, stagedSet))
      .map(r => r.item.skillbuilder_id as number)
    setCheckedIds(prev => {
      const next = new Set(prev)
      const allOn = ids.length > 0 && ids.every(id => next.has(id))
      for (const id of ids) {
        if (allOn) next.delete(id)
        else next.add(id)
      }
      return next
    })
  }

  // Resolved against the FULL list, not the filtered one: a tutor who ticks
  // rows, then searches for more, must not lose the first set when they add.
  const checkedRows = useMemo(() => {
    const wanted = checkedIds
    if (wanted.size === 0) return []
    const seen = new Set<number>()
    const out: BmdSkillbuilderRow[] = []
    for (const row of allRows(groupRowsByStrand(picker?.years || []))) {
      const id = row.item.skillbuilder_id
      if (id == null || !wanted.has(id) || seen.has(id)) continue
      if (!isStageable(row, stagedSet)) continue
      seen.add(id)
      out.push(row)
    }
    return out
  }, [checkedIds, picker, stagedSet])

  const handleStageChecked = () => {
    if (checkedRows.length === 0) return
    for (const row of checkedRows) stageRow(row)
    toast.success(
      `${checkedRows.length} Skill Builder${
        checkedRows.length === 1 ? "" : "s"
      } added to Skill Builders`,
      { autoClose: 2000 }
    )
    setCheckedIds(new Set())
  }

  const counts = picker?.counts

  return (
    <div className="p-2">
      {/* Which diagnostic to remediate, and a way to find one worksheet in a
          list that can run to a hundred and eighty. */}
      <div style={{ ...CARD, marginBottom: 16 }}>
        <Row>
          <Col md={6}>
            <label style={LABEL} htmlFor="bmd-sb-source">
              Build skill builders from
            </label>
            <Select
              inputId="bmd-sb-source"
              value={selected}
              onChange={setSelected}
              options={options}
              isLoading={loadingCandidates}
              isClearable
              placeholder={
                loadingCandidates
                  ? "Loading diagnostics…"
                  : options.length === 0
                  ? "No diagnostics with incorrect answers"
                  : "Select a completed diagnostic"
              }
              classNamePrefix="select2-selection"
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
              styles={SELECT_STYLES}
            />
          </Col>
          <Col md={6} className="mt-3 mt-md-0">
            <label style={LABEL} htmlFor="bmd-sb-search">
              Find a skill builder
            </label>
            <div style={{ position: "relative" }}>
              <i
                className="mdi mdi-magnify"
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: MUTED,
                  fontSize: 16,
                  pointerEvents: "none",
                }}
                aria-hidden="true"
              ></i>
              <input
                id="bmd-sb-search"
                type="text"
                className="form-control"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by skill or section"
                disabled={!picker}
                style={{
                  paddingLeft: 36,
                  minHeight: 40,
                  fontSize: 13,
                  borderColor: RULE,
                }}
              />
            </div>
          </Col>
        </Row>
      </div>

      {candidatesError ? (
        <div className="text-danger font-size-13">{candidatesError}</div>
      ) : null}
      {selected && loading ? (
        <div className="text-muted font-size-13">Loading…</div>
      ) : null}
      {error ? <div className="text-danger font-size-13">{error}</div> : null}

      {selected && picker && !loading ? (
        <>
          <BmdSkillbuilderStats
            counts={counts}
            passPercent={picker.pass_percent}
            className="mb-4"
          />

          <div style={{ ...CARD, padding: 0 }}>
            {/* Bulk add. Only ever over what the tutor has actually ticked;
                there is deliberately no "add every section" - a hundred
                worksheets in one lesson is not a plan. */}
            <div
              className="d-flex align-items-center flex-wrap gap-2"
              style={{ padding: "14px 16px" }}
            >
              <span style={{ color: INK, fontSize: 13, fontWeight: 700 }}>
                {checkedRows.length} selected
              </span>
              <span style={{ color: MUTED, fontSize: 12 }}>
                Tick as many as you like and add them together, or use Add on
                a single row.
              </span>
              <div className="d-flex align-items-center gap-2 ms-auto">
                <button
                  type="button"
                  className="btn btn-link p-0"
                  style={{
                    color: MUTED,
                    fontSize: 12.5,
                    textDecoration: "none",
                    visibility: checkedRows.length > 0 ? "visible" : "hidden",
                  }}
                  onClick={() => setCheckedIds(new Set())}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn fw-bold"
                  style={{
                    backgroundColor:
                      checkedRows.length > 0 ? ACCENT : "#e9ecf1",
                    border: `1px solid ${
                      checkedRows.length > 0 ? ACCENT : "#e9ecf1"
                    }`,
                    color: checkedRows.length > 0 ? "#fff" : MUTED,
                    borderRadius: 6,
                    padding: "9px 18px",
                    fontSize: 13,
                  }}
                  disabled={checkedRows.length === 0}
                  onClick={handleStageChecked}
                >
                  {checkedRows.length > 0
                    ? `Add ${checkedRows.length} to assignment`
                    : "Add to assignment"}
                </button>
              </div>
            </div>

            {/* Column headings. The rows are flex lines rather than a table, so
                these are positioned to match the widths declared there. */}
            <div
              className="d-flex align-items-center gap-3"
              style={{
                backgroundColor: "#f5f7fa",
                borderTop: `1px solid ${RULE}`,
                padding: "9px 14px",
                color: MUTED,
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              <span style={{ width: 16, flexShrink: 0 }} />
              <span className="flex-grow-1">Skill Builders</span>
              <span style={{ width: 120, flexShrink: 0 }}>Status</span>
              <span style={{ width: 70, flexShrink: 0 }}>Score</span>
              <span style={{ width: 176, flexShrink: 0 }} />
            </div>

            {groups.length === 0 ? (
              <div style={{ color: MUTED, fontSize: 13, padding: 20 }}>
                {trimmedSearch
                  ? "Nothing matches that search."
                  : "This diagnostic found no skills needing remediation."}
              </div>
            ) : (
              <BmdSkillbuilderStrandList
                groups={groups}
                stagedIds={stagedSet}
                checkedIds={checkedIds}
                forceOpen={isSearching}
                openStrand={openStrand}
                setOpenStrand={setOpenStrand}
                onToggleCheck={toggleCheck}
                onToggleStrand={toggleStrand}
                onAdd={handleStage}
                onPreview={setPreviewing}
              />
            )}
          </div>
        </>
      ) : null}

      <BmdSkillbuilderPreviewModal
        skillbuilderId={previewing?.skillbuilder_id ?? null}
        skillName={previewing?.skill_name}
        onClose={() => setPreviewing(null)}
      />
    </div>
  )
}

export default BmdSkillbuilderTabPanel
